import {
  APIComponentAttributes,
  APIFieldAttributes,
  APIOrganizationUser,
  ArdoqId,
} from '@ardoq/api-types';
import type { RepresentationData } from '@ardoq/data-model';
import type {
  ComponentPathData,
  ComponentTag,
  ReferenceLabel,
} from 'tabview/pagesView/types';
import type { AttachmentWithContext } from 'streams/documentArchive/types';
import { t, type I18nLocale } from '@ardoq/i18n';
import type { Locale } from '@ardoq/locale';
import { ComponentHeader } from 'tabview/pagesView/ComponentHeader';
import styled from 'styled-components';
import { ReferencePage } from 'tabview/pagesView/ReferencePage';
import {
  CONTENT_MAX_WIDTH,
  VERTICAL_LINE_WIDTH,
  horizontalSpacing,
  verticalSpacing,
} from 'tabview/pagesView/constants';
import { Description } from 'tabview/pagesView/Description';
import { Fields } from 'tabview/pagesView/Fields';
import { EditDescriptionButton } from 'tabview/pagesView/EditDescriptionButton';
import { dispatchAction } from '@ardoq/rxbeach';
import { pagesViewContextMenuClicked } from 'tabview/pagesView/actions';
import { colors, spacing } from '@ardoq/design-tokens';
import { getFeatureContext } from 'streams/featureContext$';
import { lockOperations } from 'lockedComponents/lockOperations';

const LEFT_SPACING = `${
  (parseInt(horizontalSpacing.LARGE, 10) - parseInt(VERTICAL_LINE_WIDTH, 10)) /
  2
}px`;

/* Chrome does not have a problem with content fragmentation when dealing with block elements.
Multicolumn flex layout does not generate boxes in a way that allows clean page breaks in all
browsers as of today  */

const InnerLayout = styled.div`
  width: 100%;
  padding: 0 ${horizontalSpacing.MEDIUM} 0 ${LEFT_SPACING};
`;
const OuterLayout = styled.div`
  margin: ${verticalSpacing.LARGE} 0 ${verticalSpacing.LARGE} ${LEFT_SPACING};
  position: relative;
`;

const VerticalLine = styled.svg`
  max-width: ${VERTICAL_LINE_WIDTH};
  min-width: ${VERTICAL_LINE_WIDTH};
  rect.component {
    cursor: default;
  }
  position: absolute;
  height: 100%;
`;

const DescriptionContainer = styled.div`
  display: flex;
  max-width: ${CONTENT_MAX_WIDTH};
`;

const Divider = styled.hr`
  border: 0;
  border-top: 1px solid ${colors.borderSubtle};
  margin: ${spacing.s16} 0;
`;

const ComponentHeaderAndDescriptionContainer = styled.div`
  /* avoid separation of header from the description */
  break-inside: avoid-page;
`;

const DEFAULT_REPRESENTATION: RepresentationData = {
  isImage: false,
  value: null,
};

type ComponentPageProps = {
  bypassRenderLimit: boolean;
  expandDescription: boolean;
  component: APIComponentAttributes | undefined;
  componentPath: ComponentPathData | undefined;
  displayLabel: string;
  fillColor: string;
  defaultFields: Pick<APIFieldAttributes, 'name' | 'type' | 'label' | '_id'>[];
  fields: APIFieldAttributes[];
  redactedFieldNames: Set<string>;
  references: {
    incoming: ArdoqId[];
    outgoing: ArdoqId[];
  };
  referenceLabelById: Map<ArdoqId, ReferenceLabel>;
  componentRepresentationById: Map<ArdoqId, RepresentationData | null>;
  componentPathById: Map<ArdoqId, ComponentPathData>;
  tagsByModelId: Map<ArdoqId, ComponentTag[]>;
  usersById: Record<ArdoqId, APIOrganizationUser>;
  attachmentsById: Record<ArdoqId, AttachmentWithContext>;
  hideEmptyFields: boolean;
  isLocalDateFormat: boolean;
  isViewpointMode: boolean;
  canEditDescription: boolean;
  hasWriteAccess: boolean;
  showContextMenu: boolean;
  i18nLocale: I18nLocale;
  locale: Locale;
};

const ComponentPage = ({
  bypassRenderLimit,
  component,
  componentPath,
  displayLabel,
  fillColor,
  fields,
  defaultFields,
  references,
  referenceLabelById,
  componentRepresentationById,
  componentPathById,
  tagsByModelId,
  usersById,
  attachmentsById,
  hideEmptyFields,
  expandDescription,
  isLocalDateFormat,
  isViewpointMode,
  canEditDescription,
  hasWriteAccess,
  showContextMenu,
  redactedFieldNames,
  i18nLocale,
  locale,
}: ComponentPageProps) => {
  if (!component) return null;

  const componentId = component._id;
  const description = component.description;

  return (
    <>
      <OuterLayout
        id={componentId}
        className="pages-view-component-page"
        aria-label={t(
          i18nLocale,
          'tabview:pagesView.componentDetailsAriaLabel',
          'component details'
        )}
      >
        <VerticalLine height="100%" width={VERTICAL_LINE_WIDTH}>
          <rect height="100%" width="100%" rx="2px" fill={fillColor} />
        </VerticalLine>
        <InnerLayout data-component-id={componentId}>
          <ComponentHeaderAndDescriptionContainer>
            <ComponentHeader
              componentId={componentId}
              isLocked={lockOperations.isLocked(
                component.lock,
                getFeatureContext()
              )}
              representationData={
                componentRepresentationById.get(componentId) ??
                DEFAULT_REPRESENTATION
              }
              displayLabel={displayLabel}
              fillColor={fillColor}
              externalDocuments={component.ardoq?.externalDocuments ?? []}
              componentPath={componentPath}
              hasWriteAccess={hasWriteAccess}
              showContextMenu={showContextMenu}
              i18nLocale={i18nLocale}
              onContextMenuClick={event =>
                dispatchAction(
                  pagesViewContextMenuClicked({
                    event: event.nativeEvent,
                    targets: { components: [component] },
                    isViewpointMode,
                  })
                )
              }
            />
            <DescriptionContainer>
              <Description
                text={description ?? ''}
                bypassRenderLimit={bypassRenderLimit}
                expandDescription={expandDescription}
              />
              <EditDescriptionButton
                modelId={componentId}
                isComponent
                descriptionIsEmpty={!description}
                canEditDescription={canEditDescription}
              />
            </DescriptionContainer>
          </ComponentHeaderAndDescriptionContainer>
          <Fields
            modelId={componentId}
            attributes={component}
            referenceTypeName={null}
            usersById={usersById}
            attachmentsById={attachmentsById}
            tags={tagsByModelId.get(componentId) ?? []}
            defaultFields={defaultFields}
            fields={fields}
            redactedFieldNames={redactedFieldNames}
            showHeader={true}
            hideEmptyFields={hideEmptyFields}
            isLocalDateFormat={isLocalDateFormat}
            i18nLocale={i18nLocale}
            locale={locale}
          />

          <ReferencePage
            references={references}
            referenceLabelById={referenceLabelById}
            componentRepresentationById={componentRepresentationById}
            componentPathById={componentPathById}
            tagsByModelId={tagsByModelId}
            usersById={usersById}
            attachmentsById={attachmentsById}
            bypassRenderLimit={bypassRenderLimit}
            expandDescription={expandDescription}
            defaultFields={defaultFields}
            hideEmptyFields={hideEmptyFields}
            i18nLocale={i18nLocale}
            locale={locale}
          />
        </InnerLayout>
      </OuterLayout>
      <Divider />
    </>
  );
};

export { ComponentPage };
